/**
 * Ассистент: чат с моделью про беседы и задачи.
 *
 * Модель работает на сервере и сама вызывает инструменты: читает беседы,
 * стенды и задачи, разбирает беседы, заводит и закрывает задачи. Каталог —
 * камни, цены, стенды — не правит. Над полем ввода — готовые команды для
 * частых вопросов: у стенда печатать некогда.
 *
 * Переписка хранится на устройстве: ушёл на другой экран — вернулся к ней же.
 */
import { useEffect, useRef, useState } from 'react';
import { setting, setSetting } from '../db';
import { apiFetch, syncNow } from '../sync';

interface Msg { role: 'user' | 'assistant'; content: string }

/** Готовые команды. Та, что с многоточием, только начинает фразу — дописать самому. */
const COMMANDS: Array<{ label: string; text: string; fill?: boolean }> = [
  { label: 'Обработать новые беседы', text: 'Обработай все неразобранные беседы и коротко перескажи итог.' },
  { label: 'Что на сегодня', text: 'Что мне сделать сегодня и что просрочено?' },
  { label: 'Что я обещал', text: 'Какие задачи у меня открыты и какие предложены из бесед? Сгруппируй по стендам.' },
  { label: 'Беседы без стенда', text: 'Покажи беседы без стенда и подскажи, к каким стендам их, похоже, стоит привязать.' },
  { label: 'Сводка по стенду…', text: 'Сводка по стенду ', fill: true },
];

const KEY = 'assistant.chat';

export default function Assistant({ onBack, embedded }: { onBack?: () => void; embedded?: boolean }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { void setting<Msg[]>(KEY, []).then(setMsgs); }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [msgs, busy]);

  async function send(q: string) {
    const ask = q.trim();
    if (!ask || busy) return;
    if (!navigator.onLine) { setProblem('Ассистент работает только онлайн.'); return; }
    setProblem('');
    setText('');
    const next = [...msgs, { role: 'user' as const, content: ask }];
    setMsgs(next);
    setBusy(true);
    try {
      // Сначала отправить своё: иначе ассистент не увидит беседу, записанную только что.
      await syncNow();
      const res = await apiFetch('/api/assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next.slice(-20), tzOffset: -new Date().getTimezoneOffset() }),
      });
      const d = (await res.json().catch(() => ({}))) as { reply?: string; changed?: boolean; error?: string };
      if (!res.ok || !d.reply) {
        setProblem(d.error ?? 'Ассистент не ответил. Попробуйте ещё раз.');
        if (d.changed) void syncNow();
        return;
      }
      const all = [...next, { role: 'assistant' as const, content: d.reply }].slice(-40);
      setMsgs(all);
      await setSetting(KEY, all);
      // Ассистент что-то поменял — разобрал беседы, завёл задачи: забрать к себе.
      if (d.changed) void syncNow();
    } catch {
      setProblem('Нет связи с сервером.');
    } finally {
      setBusy(false);
    }
  }

  function command(c: typeof COMMANDS[number]) {
    if (c.fill) {
      setText(c.text);
      input.current?.focus();
    } else {
      void send(c.text);
    }
  }

  async function clear() {
    setMsgs([]);
    await setSetting(KEY, []);
  }

  const chat = (
    <>
      {msgs.length === 0 && !busy && (
        <div className="blankslate">
          <b>Спросите про беседы и задачи</b>
          Ассистент разберёт беседы, найдёт цены и договорённости, заведёт и закроет задачи.
          Камни и цены он не меняет — это делается кнопками в выжимке беседы.
        </div>
      )}
      <div className="chatlog">
        {msgs.map((m, i) => <p key={i} className={`chatmsg ${m.role}`}>{m.content}</p>)}
        {busy && <p className="chatmsg assistant thinking">Думаю…</p>}
        <div ref={end} />
      </div>
      {problem && <div className="memo warn">{problem}</div>}
    </>
  );

  const composer = (
    <div className="chatbar">
      <div className="chatcmds">
        {COMMANDS.map((c) => (
          <button key={c.label} className="state-btn" disabled={busy} onClick={() => command(c)}>{c.label}</button>
        ))}
      </div>
      <div className="chatinput">
        <textarea ref={input} rows={1} value={text} placeholder="Вопрос или просьба"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(text); } }} />
        <button className="btn btn-primary btn-sm" disabled={busy || !text.trim()} onClick={() => void send(text)}>
          Отправить
        </button>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="desk-chat">
        <div className="desk-scroll">
          <div className="desk-head">
            <h1>Ассистент</h1>
            <p>Беседы и задачи: разобрать, найти договорённости, завести и закрыть задачи.
              {msgs.length > 0 && <> <button className="linky" onClick={() => void clear()}>Начать заново</button></>}
            </p>
          </div>
          <div style={{ maxWidth: 760 }}>{chat}</div>
        </div>
        <div className="desk-chatbar">{composer}</div>
      </div>
    );
  }

  return (
    <div className="app chatapp">
      <div className="bar">
        {onBack && <button className="crumb" onClick={onBack}>‹ Ещё</button>}
        <h1 className="t">Ассистент</h1>
        {msgs.length > 0 && <button className="linky" onClick={() => void clear()}>начать заново</button>}
      </div>
      <div className="scroll">{chat}</div>
      {composer}
    </div>
  );
}
